import { IconFa, Switch } from '@/components/wm';
import { cn, getStoreResource } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'holiday'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const QUARTERS = [0, 15, 30, 45];

const defineSchedule = data => {
    if (!data.ids || data.ids.length === 0) {
        return DAYS.reduce((acc, day) => {
            acc[day] = Array(24 * 4).fill('off');
            return acc;
        }, {});
    };

    if (data.schedule && Object.keys(data.schedule).length > 0) {
        return data.schedule;
    }

    const frequencyMap = {};

    data.ids.forEach(id => {
        const alarm = getStoreResource('alarms', id);

        if (!alarm || !alarm.schedule) {
            return;
        }

        const scheduleKey = JSON.stringify(alarm.schedule);

        // Increment frequency count
        frequencyMap[scheduleKey] = (frequencyMap[scheduleKey] || 0) + 1;
    });

    let mostCommonKey = null;
    let maxCount = 0;

    for (const [key, count] of Object.entries(frequencyMap)) {
        if (count > maxCount) {
            maxCount = count;
            mostCommonKey = key;
        }
    }

    return mostCommonKey ? JSON.parse(mostCommonKey) : DAYS.reduce((acc, day) => {
        acc[day] = Array(24 * 4).fill('off');
        return acc;
    }, {});
};

const defineUnit = data => {
    const
        indicatorKindId = data.indicatorKind || (data.indicatorId ? getStoreResource('indicators', data.indicatorId)?.kind_id : null),
        indicatorKind = getStoreResource('indicatorKinds', indicatorKindId);

    if (indicatorKind) {
        const unit = getStoreResource('units', indicatorKind.unit_id)?.symbol;

        if (unit === 'Wh') {
            return 'kWh';
        }

        return unit;
    }

    return '';
};

const defineValue = (data, field, unit) => {
    if (!data.ids || data.ids.length === 0) {
        return 0;
    };

    const valueMap = {};

    data.ids.forEach(id => {
        const alarm = getStoreResource('alarms', id);

        if (!alarm || alarm[field] === undefined) {
            return;
        }

        const valueKey = alarm[field];
        valueMap[valueKey] = (valueMap[valueKey] || 0) + 1;
    });

    let mostCommonKey = null;
    let maxCount = 0;

    for (const [key, count] of Object.entries(valueMap)) {
        if (count > maxCount) {
            maxCount = count;
            mostCommonKey = key;
        }
    }

    if (unit === 'kWh') {
        // Convert Wh to kWh if needed
        mostCommonKey = mostCommonKey / 1000;
    }

    return mostCommonKey !== null ? mostCommonKey : 0;
};

export function Schedule({ data, onChangeField, onCompleted }) {
    const unit = defineUnit(data);

    const formSchema = z.object({
        minValue: z.coerce.number().min(0, 'Min value must be at least 0'),
        maxValue: z.coerce.number().min(0, 'Max value must be at least 0')
    }).refine(data => data.maxValue >= data.minValue, {
        message: 'Max value must be greater than or equal to Min value',
        path: ['maxValue'] // This attaches the error to the maxValue field specifically
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            changeSchedule: false,
            changeValues: false,
            schedule: (data.schedule && Object.keys(data.schedule).length > 0)
                ? data.schedule
                : defineSchedule(data),
            minValue: data.minValue ? data.minValue : defineValue(data, 'min_value', unit),
            maxValue: data.maxValue ? data.maxValue : defineValue(data, 'max_value', unit)
        },
        mode: 'onChange' // Validate as the user types
    });

    const
        changeSchedule = form.watch('changeSchedule'),
        changeValues = form.watch('changeValues'),
        currentMaxValue = form.watch('maxValue'),
        currentMinValue = form.watch('minValue'),
        currentSchedule = form.watch('schedule');

    const isBulkEdit = data.ids && data.ids.length > 0;
    const isScheduleDisabled = isBulkEdit && !changeSchedule;
    const isValuesDisabled = isBulkEdit && !changeValues;

    const
        [dragEnd, setDragEnd] = useState(null),
        [dragStart, setDragStart] = useState(null),
        [selectedPeriod, setSelectedPeriod] = useState('on');

    // HELPER: Determine if a specific cell is within the current drag rectangle
    const isCellSelected = (dayIdx, slotIdx) => {
        if (!dragStart || !dragEnd) {
            return false;
        }

        const minDay = Math.min(dragStart.dayIdx, dragEnd.dayIdx);
        const maxDay = Math.max(dragStart.dayIdx, dragEnd.dayIdx);
        const minSlot = Math.min(dragStart.slotIdx, dragEnd.slotIdx);
        const maxSlot = Math.max(dragStart.slotIdx, dragEnd.slotIdx);

        return (
            dayIdx >= minDay &&
            dayIdx <= maxDay &&
            slotIdx >= minSlot &&
            slotIdx <= maxSlot
        );
    };

    const handleMouseDown = (dayIdx, slotIdx) => {
        if (!selectedPeriod || isScheduleDisabled) {
            return;
        }
        setDragStart({ dayIdx, slotIdx });
        setDragEnd({ dayIdx, slotIdx });
    };

    const handleMouseEnter = (dayIdx, slotIdx) => {
        if (dragStart && !isScheduleDisabled) {
            setDragEnd({ dayIdx, slotIdx });
        }
    };

    const handleMouseUp = () => {
        if (dragStart && dragEnd && selectedPeriod && !isScheduleDisabled) {
            const newSched = { ...currentSchedule };

            const minDay = Math.min(dragStart.dayIdx, dragEnd.dayIdx);
            const maxDay = Math.max(dragStart.dayIdx, dragEnd.dayIdx);
            const minSlot = Math.min(dragStart.slotIdx, dragEnd.slotIdx);
            const maxSlot = Math.max(dragStart.slotIdx, dragEnd.slotIdx);

            // Apply the selected period to all cells in the range
            for (let d = minDay; d <= maxDay; d++) {
                const dayName = DAYS[d];
                const dayArray = [...newSched[dayName]];
                for (let s = minSlot; s <= maxSlot; s++) {
                    dayArray[s] = selectedPeriod;
                }
                newSched[dayName] = dayArray;
            }

            form.setValue('schedule', newSched, { shouldDirty: true });

            onChangeField('schedule')(newSched);
        }
        setDragStart(null);
        setDragEnd(null);
    };

    useEffect(() => {
        if (!isBulkEdit) {
            onCompleted(form.formState.isValid);
            return;
        }

        const hasAnyChange = data.changeName || data.changeUserIds || changeSchedule || changeValues;
        onCompleted(form.formState.isValid && hasAnyChange);
    }, [form.formState.isValid, isBulkEdit, changeSchedule, changeValues, data.changeName, data.changeUserIds, onCompleted]);

    // Synchronization for Min/Max values
    useEffect(() => {
        if (form.formState.isValid) {
            const min = isValuesDisabled ? null : currentMinValue;
            const max = isValuesDisabled ? null : currentMaxValue;
            onChangeField('minValue')(min);
            onChangeField('maxValue')(max);
        }
    }, [currentMinValue, currentMaxValue, form.formState.isValid, isValuesDisabled]);

    useEffect(() => {
        window.addEventListener('mouseup', handleMouseUp);
        return () => window.removeEventListener('mouseup', handleMouseUp);
    }, [dragStart, dragEnd, selectedPeriod, isScheduleDisabled]);

    // Define empty schedule for "blank" display
    const emptySchedule = useMemo(() => DAYS.reduce((acc, day) => {
        acc[day] = Array(24 * 4).fill('off');
        return acc;
    }, {}), []);

    // Most common assets for resetting/pre-filling
    const mostCommonSchedule = useMemo(() => defineSchedule(data), [data.ids]);
    const mostCommonMinValue = useMemo(() => defineValue(data, 'min_value', unit), [data.ids]);
    const mostCommonMaxValue = useMemo(() => defineValue(data, 'max_value', unit), [data.ids]);

    return (
        <div className="tw-relative tw-flex tw-flex-1 tw-w-full">
            {/* Overlay for baseline type */}
            {data.type === 'baseline' && (
                <div className="tw-absolute tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-white/80 tw-backdrop-blur-sm">
                    <div className="tw-bg-background tw-p-8 tw-rounded-xl tw-shadow-xl tw-border tw-border-solid tw-border-border tw-max-w-md tw-text-center">
                        <h3 className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mb-2">
                            Baseline Alarm
                        </h3>
                        <p className="tw-text-sm tw-text-gray-600 tw-leading-relaxed">
                            You chose the baseline alarm type. The schedule and value will be automatically generated (use the default values for these fields).
                        </p>
                    </div>
                </div>
            )}

            {/* Overlay for disabled Bulk Edit Schedule Drawing */}
            {isScheduleDisabled && (
                <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-w-[65%] tw-z-40 tw-flex tw-items-center tw-justify-center tw-bg-white/40 tw-backdrop-blur-[1px] tw-pointer-events-none">
                    <div className="tw-bg-background/90 tw-px-6 tw-py-3 tw-rounded-full tw-shadow-lg tw-border tw-border-solid tw-border-border tw-flex tw-items-center tw-gap-2">
                        <IconFa color="default" icon="lock" size="small"/>
                        <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                            {i18next.t('alarms.keep_original', 'Keep original values')}
                        </span>
                    </div>
                </div>
            )}

            <div className="tw-flex tw-flex-col tw-w-[65%] tw-bg-background tw-text-gray-700">
                {/* HEADER: Fixed height, won't shrink */}
                <div className="tw-flex tw-flex-none tw-bg-background-grey">
                    <div className="tw-w-12 tw-flex-shrink-0 tw-p-1 tw-text-[10px] tw-font-bold tw-flex tw-items-center tw-justify-center">
                        HR
                    </div>
                    <div className="tw-flex tw-flex-grow">
                        {DAYS.map(day => (
                            <div
                                className={`tw-border tw-border-solid tw-border-border tw-flex-1 tw-text-center tw-py-1 tw-text-[10px] tw-font-bold tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis`}
                                key={day}
                            >
                                {i18next.t(`weekdays.${day}`)}
                                <div className="tw-flex tw-mt-0.5 tw-font-normal tw-text-[8px] tw-text-gray-400">
                                    {QUARTERS.map(q => <div className="tw-flex-1" key={q}>{q}</div>)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="tw-flex">

                    {/* Vertical Hour Labels: Flex-col with flex-1 on items makes them scale */}
                    <div className="
                        tw-w-12 tw-flex-shrink-0 tw-bg-background-grey tw-flex
                        tw-flex-col
                        tw-border tw-border-solid tw-border-border
                    ">
                        {HOURS.map(hour => (
                            <div
                                className="tw-flex tw-items-center tw-justify-center tw-text-[8px]
                                            tw-border-b tw-border-solid tw-border-border/30
                                            tw-flex-1"
                                key={hour}
                            >
                                {`${String(hour).padStart(2, '0')}:00`}
                            </div>
                        ))}
                    </div>

                    {/* The Main Grid: Flex-grow and flex-col for rows */}
                    <div className="tw-flex tw-flex-grow">
                        {DAYS.map((day, dayIndex) => (
                            <div className={`tw-border-r tw-border-b tw-border-solid tw-border-border tw-flex-1 tw-flex tw-flex-col`} key={day}>
                                {HOURS.map(hour => (
                                    <div
                                        className="tw-grid tw-grid-cols-4" key={`${day}-${hour}`}
                                    >
                                        {/* 15-minute Vertical Sub-divisions */}
                                        {QUARTERS.map((quarter, qIndex) => {
                                            const slotIndex = (hour * 4) + qIndex;
                                            const scheduleToDisplay = isScheduleDisabled ? emptySchedule : currentSchedule;
                                            const slotValue = scheduleToDisplay[day]?.[slotIndex] || 'off';
                                            const activeClass = slotValue !== 'off' ? 'tw-bg-secondary' : '';

                                            const isHighlight = isCellSelected(dayIndex, slotIndex);

                                            return (
                                                <div
                                                    className={cn(
                                                        'tw-aspect-square tw-relative tw-border-r tw-border-b tw-border-solid tw-border-border/20 tw-transition-colors',
                                                        !isScheduleDisabled && 'hover:tw-bg-accent tw-cursor-pointer',
                                                        activeClass
                                                    )}
                                                    key={quarter}
                                                    onMouseDown={() => handleMouseDown(dayIndex, slotIndex)}
                                                    onMouseEnter={() => handleMouseEnter(dayIndex, slotIndex)}
                                                >
                                                    {isHighlight && (
                                                        <div className="tw-absolute tw-inset-0 tw-bg-accent/40 tw-border tw-border-accent tw-z-10"/>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* RIGHT: Values Manager Sidebar (Fixed width) */}
            <div className="tw-w-[35%] tw-flex-none tw-border-l tw-bg-background tw-pl-4 tw-flex tw-flex-col tw-gap-4">
                {/* Period Selection Section */}
                <div>
                    <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                        <h3 className="tw-font-bold tw-text-gray-700 !tw-m-0">{i18next.t('glossary.periods').capitalize()}</h3>
                        {isBulkEdit && (
                            <Switch
                                checked={changeSchedule}
                                label={i18next.t('common.change', 'Change?')}
                                onCheckedChange={checked => {
                                    form.setValue('changeSchedule', checked);
                                    if (onChangeField) {
                                        onChangeField('changeSchedule')(checked);
                                    }
                                    if (checked) {
                                        form.setValue('schedule', mostCommonSchedule);
                                        onChangeField('schedule')(mostCommonSchedule);
                                    } else {
                                        form.setValue('schedule', emptySchedule);
                                        onChangeField('schedule')({});
                                    }
                                }}
                            />
                        )}
                    </div>

                    {/* Unified Period Toggle */}
                    <div className={cn(
                        'tw-flex tw-rounded-lg tw-overflow-hidden tw-border tw-border-solid tw-border-border tw-bg-background-grey',
                        isScheduleDisabled && 'tw-opacity-50'
                    )}>
                        <button
                            className={cn(
                                'tw-flex-1 tw-py-2 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all tw-border tw-border-solid',
                                selectedPeriod === 'off'
                                    ? 'tw-bg-primary tw-border-primary tw-text-white tw-font-bold'
                                    : 'tw-text-slate-500 tw-border-transparent',
                                isScheduleDisabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
                            )}
                            onClick={() => !isScheduleDisabled && setSelectedPeriod('off')}
                            type="button"
                        >
                            <span className="tw-text-sm">{i18next.t('common.off').capitalize()}</span>
                        </button>
                        <button
                            className={cn(
                                'tw-flex-1 tw-py-2 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all tw-border tw-border-solid',
                                selectedPeriod === 'on'
                                    ? 'tw-bg-primary tw-border-primary tw-text-white tw-font-bold'
                                    : 'tw-text-slate-500 tw-border-transparent',
                                isScheduleDisabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
                            )}
                            onClick={() => !isScheduleDisabled && setSelectedPeriod('on')}
                            type="button"
                        >
                            <IconFa icon="paint-brush" size="xsmall"/>
                            <span className="tw-text-sm">{i18next.t('common.on').capitalize()}</span>
                        </button>
                    </div>
                </div>

                {/* Values Configuration Section */}
                <div>
                    <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                        <h3 className="tw-font-bold tw-text-gray-700 !tw-m-0">{i18next.t('common.values').capitalize()}</h3>
                        {isBulkEdit && (
                            <Switch
                                checked={changeValues}
                                label={i18next.t('common.change', 'Change?')}
                                onCheckedChange={checked => {
                                    form.setValue('changeValues', checked);
                                    if (onChangeField) {
                                        onChangeField('changeValues')(checked);
                                    }
                                    if (checked) {
                                        form.setValue('minValue', mostCommonMinValue);
                                        form.setValue('maxValue', mostCommonMaxValue);
                                    } else {
                                        form.setValue('minValue', 0);
                                        form.setValue('maxValue', 0);
                                    }
                                }}
                            />
                        )}
                    </div>

                    <div className={cn(
                        'tw-p-4 tw-border tw-border-solid tw-border-border tw-rounded-lg tw-bg-white tw-shadow-sm tw-flex tw-gap-3',
                        isValuesDisabled && 'tw-opacity-50'
                    )}>
                        <div className="tw-flex-1">
                            <label className="tw-text-[12px] tw-text-gray-400 tw-block tw-mb-1">Min ({unit})</label>
                            <input
                                className="tw-h-9 tw-w-full tw-text-[14px] tw-border tw-border-solid tw-border-border tw-rounded-md tw-px-2 focus:tw-border-primary focus:tw-outline-none"
                                disabled={isValuesDisabled}
                                onChange={e => form.setValue('minValue', e.target.value, { shouldValidate: true })}
                                placeholder={isValuesDisabled ? i18next.t('alarms.keep_original') : ''}
                                type="number"
                                value={isValuesDisabled ? '' : currentMinValue}
                            />
                        </div>
                        <div className="tw-flex-1">
                            <label className="tw-text-[12px] tw-text-gray-400 tw-block tw-mb-1">Max ({unit})</label>
                            <input
                                className="tw-h-9 tw-w-full tw-text-[14px] tw-border tw-border-solid tw-border-border tw-rounded-md tw-px-2 focus:tw-border-primary focus:tw-outline-none"
                                disabled={isValuesDisabled}
                                onChange={e => form.setValue('maxValue', e.target.value, { shouldValidate: true })}
                                placeholder={isValuesDisabled ? i18next.t('alarms.keep_original') : ''}
                                type="number"
                                value={isValuesDisabled ? '' : currentMaxValue}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
